import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Euro, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface PriceTrackerProps {
  productId: string;
  onPriceUpdate?: () => void;
}

interface PriceHistory {
  id: string;
  price: number;
  tracked_at: string;
  notes?: string;
}

interface Product {
  price: number;
  created_at: string;
}

export const PriceTracker = ({ productId, onPriceUpdate }: PriceTrackerProps) => {
  const { user } = useAuth();
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [originalProduct, setOriginalProduct] = useState<Product | null>(null);
  const [newPrice, setNewPrice] = useState('');
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPriceHistory();
    fetchOriginalProduct();
  }, [productId]);

  const fetchOriginalProduct = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('price, created_at')
        .eq('id', productId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching original product:', error);
        return;
      }
      setOriginalProduct(data);
    } catch (error) {
      console.error('Error fetching original product:', error);
    }
  };

  const fetchPriceHistory = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('price_history')
        .select('*')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .order('tracked_at', { ascending: true });

      if (error) throw error;
      setPriceHistory(data || []);
    } catch (error) {
      console.error('Error fetching price history:', error);
      toast.error('Fehler beim Laden des Preisverlaufs');
    } finally {
      setLoading(false);
    }
  };

  const addPricePoint = async () => {
    if (!user || !newPrice.trim()) return;
    
    try {
      const price = parseFloat(newPrice.replace(',', '.'));
      if (isNaN(price)) {
        toast.error('Bitte geben Sie einen gültigen Preis ein');
        return;
      }

      const { error } = await supabase
        .from('price_history')
        .insert([
          {
            product_id: productId,
            user_id: user.id,
            price,
            notes: newNote.trim() || null
          }
        ]);

      if (error) throw error;

      setNewPrice('');
      setNewNote('');
      fetchPriceHistory();
      toast.success('Preis hinzugefügt');
      if (onPriceUpdate) {
        onPriceUpdate();
      }
    } catch (error) {
      console.error('Error adding price:', error);
      toast.error('Fehler beim Hinzufügen des Preises');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      addPricePoint();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const chartData = originalProduct ? [
    {
      date: formatDate(originalProduct.created_at),
      price: originalProduct.price,
      notes: 'Ursprungspreis'
    },
    ...priceHistory.map(point => ({
      date: formatDate(point.tracked_at),
      price: point.price,
      notes: point.notes
    }))
  ] : [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-lg font-bold text-emerald-600">
            {new Intl.NumberFormat('de-DE', {
              style: 'currency',
              currency: 'EUR'
            }).format(payload[0].value)}
          </p>
          {payload[0].payload.notes && (
            <p className="text-xs text-gray-500 mt-1 italic">{payload[0].payload.notes}</p>
          )}
        </div>
      );
    }
    return null;
  };

  const formatYAxisTick = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getYAxisDomain = () => {
    if (chartData.length === 0) return [0, 100];
    
    const prices = chartData.map(item => item.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const padding = (maxPrice - minPrice) * 0.1; // 10% padding
    
    return [
      Math.max(0, minPrice - padding),
      maxPrice + padding
    ];
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
        <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
        Preisverlauf
      </h3>
      
      <div className="flex gap-2">
        <div className="flex-1 flex gap-2">
          <Input
            type="text"
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Neuer Preis (€)"
            className="text-sm"
          />
          <Input
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Notiz (optional)"
            className="text-sm"
          />
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addPricePoint();
            }} 
            size="sm" 
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {chartData.length > 0 && (
        <>
          <div className="h-64 w-full bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  dy={10}
                />
                <YAxis 
                  domain={getYAxisDomain()}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={formatYAxisTick}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke="#059669" 
                  strokeWidth={3}
                  dot={{ 
                    fill: '#059669', 
                    strokeWidth: 2, 
                    r: 6,
                    stroke: '#ffffff'
                  }}
                  activeDot={{ 
                    r: 8, 
                    fill: '#059669',
                    stroke: '#ffffff',
                    strokeWidth: 3
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2 max-h-32 overflow-y-auto">
            {chartData.map((point, index) => (
              <div key={index} className="bg-gradient-to-r from-gray-50 to-white p-3 rounded-lg border border-gray-100 flex items-center justify-between hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-emerald-600 rounded-full flex-shrink-0"></div>
                  <div>
                    <span className="font-semibold text-emerald-700">
                      {new Intl.NumberFormat('de-DE', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format(point.price)}
                    </span>
                    {point.notes && (
                      <span className="text-gray-600 ml-2 text-sm">- {point.notes}</span>
                    )}
                  </div>
                </div>
                <span className="text-gray-500 text-xs font-medium">
                  {point.date}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}; 